import * as THREE from 'three';
import { Line2 } from 'three/examples/jsm/lines/Line2.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js';
import { FikoytKok3DElement } from '../fikoyt-kok-3d-element';
import { FikoSahne } from '../../FikoSahne';
import { getCanvasResolution, getCanvasResolutionVec2 } from '../../FikoCanvasResolution';

// TWEEN global olarak yükleniyor (FikoThreeJS tarafından)
declare var TWEEN: any;

// ============================================================================
// TİPLER VE ARAYÜZLER
// ============================================================================

/** Çizgi stili */
export type CizgiStili = 'duz' | 'kesikli' | 'noktali' | 'kesikli-noktali';

/** Ok tipi */
export type OkTipi = 'yok' | 'ok' | 'ucgen' | 'kare' | 'daire';

/** Çizgi render tipi */
export type CizgiTipi = 'Line2' | 'Line' | 'LineSegments';

/** Çizgi oluşturma yöntemi */
export type CizgiYontemi = 'noktadanNoktaya' | 'noktadanYonUzunluk' | 'merkezdenCift' | 'noktadanAciUzunluk' | 'noktadan3DAci' | 'yatay' | 'dikey' | 'derinlik';

/** Çizgi ayarları */
export interface ICizgiAyarlari {
    renk?: number | string;
    kalinlik?: number;
    stil?: CizgiStili;
    kesikliAralik?: number;
    kesikliBoyut?: number;
    opaklık?: number;
    okBaslangic?: OkTipi;
    okBitis?: OkTipi;
    okBoyut?: number;
    secilebilir?: boolean;
    katman?: number;
    golpiKullak?: boolean;
    cizgiTipi?: CizgiTipi;
    enUstte?: boolean;
    renderEt?: boolean;
    yontem?: CizgiYontemi;
}

/** Varsayılan çizgi ayarları */
const VARSAYILAN_AYARLAR: ICizgiAyarlari = {
    renk: 0x00ff00,
    kalinlik: 2,
    stil: 'duz',
    kesikliAralik: 5,
    kesikliBoyut: 10,
    opaklık: 1,
    okBaslangic: 'yok',
    okBitis: 'yok',
    okBoyut: 5,  // Daha görünür ok boyutu (mm cinsinden)
    secilebilir: true,
    katman: 0,
    golpiKullak: false,
    cizgiTipi: 'Line2',
    enUstte: false,
    renderEt: true,
    yontem: 'noktadanNoktaya'
};

// ============================================================================
// HANDLE SİSTEMİ (CATIA-Style Draggable Handles)
// ============================================================================

/**
 * Çizgi uç noktalarında sürüklenebilir handle'lar
 * THREE.Points kullanarak zoom'dan bağımsız sabit boyutlu noktalar
 */
export class FikoYT3dLineHandles extends THREE.Group {
    /** Bağlı çizgi referansı */
    private cizgi: FikoYT3dLine;

    /** Points objesi - tüm handle noktaları */
    private _points: THREE.Points | null = null;

    /** Points geometrisi */
    private _geometry: THREE.BufferGeometry | null = null;

    /** Points materyali */
    private _material: THREE.PointsMaterial | null = null;

    /** Sürükleme durumu */
    isDragging: boolean = false;

    /** Hangi handle sürükleniyor */
    dragTarget: 'baslangic' | 'bitis' | 'orta' | null = null;

    /** Handle boyutu (pixel) */
    private handleBoyut: number = 12;

    /** Hit radius for raycast (world units) */
    private hitRadius: number = 8;

    /** Hover edilen handle tip */
    private _hoverTip: 'baslangic' | 'bitis' | 'orta' | null = null;

    /** merkezdenCift yöntemi için orta handle var mı */
    private _ortaHandleVar: boolean = false;

    /** Değişiklik callback'i */
    onChange?: (baslangic: THREE.Vector3, bitis: THREE.Vector3) => void;

    constructor(cizgi: FikoYT3dLine) {
        super();
        this.cizgi = cizgi;
        this.name = 'LineHandles_' + cizgi.uuid;

        // Çift tıklama delegasyonu
        const cizgiRef = cizgi;
        (this as any).ciftTiklandi = () => {
            if (typeof cizgiRef?.ciftTiklandi === 'function') {
                cizgiRef.ciftTiklandi();
            }
        };

        // Handle boyutunu kalınlığa göre ayarla
        this.handleBoyut = Math.max(10, Math.min(16, cizgi.kalinlik * 3));
        this.hitRadius = Math.max(5, cizgi.kalinlik * 2);

        // merkezdenCift yöntemi için orta handle
        this._ortaHandleVar = cizgi.yontem === 'merkezdenCift';

        // Points oluştur
        this.pointsOlustur();

        // Render order - her zaman üstte
        this.renderOrder = 1000;
    }

    /** Eski API uyumluluğu için getter'lar */
    get baslangicHandle(): THREE.Object3D | null { return this._points; }
    get bitisHandle(): THREE.Object3D | null { return this._points; }
    get ortaHandle(): THREE.Object3D | null { return this._ortaHandleVar ? this._points : null; }

    /**
     * Points objesini oluştur
     */
    private pointsOlustur(): void {
        // Mevcut points'i temizle
        this.handleTemizle();

        const noktaSayisi = this._ortaHandleVar ? 3 : 2; // baslangic, bitis, (orta)

        // Geometry oluştur
        this._geometry = new THREE.BufferGeometry();

        // Pozisyonlar
        const positions = new Float32Array(noktaSayisi * 3);
        const baslangic = this.cizgi.baslangic;
        const bitis = this.cizgi.bitis;

        positions[0] = baslangic.x;
        positions[1] = baslangic.y;
        positions[2] = baslangic.z;

        positions[3] = bitis.x;
        positions[4] = bitis.y;
        positions[5] = bitis.z;

        if (this._ortaHandleVar) {
            const orta = this.cizgi.ortaNokta;
            positions[6] = orta.x;
            positions[7] = orta.y;
            positions[8] = orta.z;
        }

        this._geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));

        // Renkler - başlangıç mavi, bitiş kırmızı, orta sarı
        const colors = new Float32Array(noktaSayisi * 3);
        // Mavi - başlangıç
        colors[0] = 0x34 / 255; colors[1] = 0x98 / 255; colors[2] = 0xdb / 255;
        // Kırmızı - bitiş
        colors[3] = 0xe7 / 255; colors[4] = 0x4c / 255; colors[5] = 0x3c / 255;
        // Sarı - orta
        if (this._ortaHandleVar) {
            colors[6] = 0xf1 / 255; colors[7] = 0xc4 / 255; colors[8] = 0x0f / 255;
        }

        this._geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));

        // Material - sizeAttenuation: false ile zoom'dan bağımsız
        this._material = new THREE.PointsMaterial({
            size: this.handleBoyut,
            sizeAttenuation: false,  // Zoom'dan etkilenmez!
            vertexColors: true,
            depthTest: false,
            depthWrite: false,
            transparent: true,
            opacity: 0.9
        });

        // Points objesi
        this._points = new THREE.Points(this._geometry, this._material);
        this._points.name = 'LineHandlePoints';
        this._points.frustumCulled = false;
        this._points.renderOrder = 1002;
        this._points.layers.enableAll();

        // Çizgi referansı ekle
        this._points.userData['lineHandles'] = this;
        this._points.userData['secilebilir'] = true;

        // Çift tıklama delegasyonu
        const cizgiRef = this.cizgi;
        (this._points as any).ciftTiklandi = () => {
            if (typeof cizgiRef?.ciftTiklandi === 'function') {
                cizgiRef.ciftTiklandi();
            }
        };

        this.add(this._points);
        this.updateMatrixWorld(true);
    }

    /**
     * Handle'ları temizle
     */
    private handleTemizle(): void {
        if (this._points) {
            this.remove(this._points);
            this._points = null;
        }
        if (this._geometry) {
            this._geometry.dispose();
            this._geometry = null;
        }
        if (this._material) {
            this._material.dispose();
            this._material = null;
        }
    }

    /**
     * Handle pozisyonlarını güncelle
     */
    guncelle(): void {
        if (!this.cizgi || !this._geometry) return;

        const posAttr = this._geometry.getAttribute('position') as THREE.BufferAttribute;
        if (!posAttr) {
            this.pointsOlustur();
            return;
        }

        const baslangic = this.cizgi.baslangic;
        const bitis = this.cizgi.bitis;

        posAttr.setXYZ(0, baslangic.x, baslangic.y, baslangic.z);
        posAttr.setXYZ(1, bitis.x, bitis.y, bitis.z);

        if (this._ortaHandleVar && posAttr.count >= 3) {
            const orta = this.cizgi.ortaNokta;
            posAttr.setXYZ(2, orta.x, orta.y, orta.z);
        }

        // Buffer güncelleme bayrağı
        posAttr.needsUpdate = true;

        // Geometry bounding sphere'i yeniden hesapla (raycast için gerekli)
        this._geometry.computeBoundingSphere();

        // Points ve Group matrislerini güncelle
        if (this._points) {
            this._points.updateMatrixWorld(true);
        }
        this.updateMatrixWorld(true);
    }

    /**
     * Hover efekti uygula
     */
    setHover(handle: THREE.Object3D | null): void {
        // Points için hover - şimdilik sadece tip'i saklıyoruz
        if (handle && handle.userData['handleTip']) {
            this._hoverTip = handle.userData['handleTip'];
        } else {
            this._hoverTip = null;
        }
    }

    /**
     * Hover edilen handle index'ini ayarla (tip ile)
     */
    setHoverByTip(tip: 'baslangic' | 'bitis' | 'orta' | null): void {
        this._hoverTip = tip;
    }

    /**
     * Raycast için Points döndür
     */
    getRaycastTargets(): THREE.Object3D[] {
        return this._points ? [this._points] : [];
    }

    /**
     * Manuel küre kesişim testi - ray ile handle bul
     */
    manuelKureKesisim(ray: THREE.Ray): { hit: boolean; handleTip: string | null; mesafe: number } {
        if (!this._geometry || !this._points) {
            return { hit: false, handleTip: null, mesafe: Infinity };
        }

        // Matrisleri güncelle
        this._points.updateMatrixWorld(true);
        this.updateMatrixWorld(true);

        let enYakinMesafe = Infinity;
        let bulunanTip: string | null = null;

        const posAttr = this._geometry.getAttribute('position') as THREE.BufferAttribute;
        if (!posAttr) {
            return { hit: false, handleTip: null, mesafe: Infinity };
        }

        const noktalar = [
            { tip: 'baslangic', index: 0 },
            { tip: 'bitis', index: 1 }
        ];

        if (this._ortaHandleVar && posAttr.count >= 3) {
            noktalar.push({ tip: 'orta', index: 2 });
        }

        for (const { tip, index } of noktalar) {
            const pos = new THREE.Vector3(
                posAttr.getX(index),
                posAttr.getY(index),
                posAttr.getZ(index)
            );

            // Local'den world koordinatlarına dönüştür
            this._points.localToWorld(pos);

            const sphere = new THREE.Sphere(pos, this.hitRadius);
            const intersect = new THREE.Vector3();

            if (ray.intersectSphere(sphere, intersect)) {
                const mesafe = ray.origin.distanceTo(intersect);
                if (mesafe < enYakinMesafe) {
                    enYakinMesafe = mesafe;
                    bulunanTip = tip;
                }
            }
        }

        return {
            hit: bulunanTip !== null,
            handleTip: bulunanTip,
            mesafe: enYakinMesafe
        };
    }

    /**
     * Ekran koordinatından handle index bul
     */
    getHandleAtScreenPosition(screenX: number, screenY: number, camera: THREE.Camera, canvasWidth: number, canvasHeight: number): { tip: string | null; index: number } {
        if (!this._geometry || !this._points) return { tip: null, index: -1 };

        // Geometry güncellenmesini zorla
        this._geometry.computeBoundingSphere();

        const posAttr = this._geometry.getAttribute('position') as THREE.BufferAttribute;
        if (!posAttr) return { tip: null, index: -1 };

        const noktalar = [
            { tip: 'baslangic', index: 0 },
            { tip: 'bitis', index: 1 }
        ];

        if (this._ortaHandleVar && posAttr.count >= 3) {
            noktalar.push({ tip: 'orta', index: 2 });
        }

        let enYakinMesafe = Infinity;
        let bulunan: { tip: string | null; index: number } = { tip: null, index: -1 };

        // Points matrixi güncelle
        this._points.updateMatrixWorld(true);

        for (const { tip, index } of noktalar) {
            const pos = new THREE.Vector3(
                posAttr.getX(index),
                posAttr.getY(index),
                posAttr.getZ(index)
            );

            // Local'den world koordinatlarına dönüştür
            this._points.localToWorld(pos);

            // World to screen (NDC)
            pos.project(camera);

            // Kamera arkasındaki noktaları atla
            if (pos.z > 1) continue;

            const sx = (pos.x + 1) / 2 * canvasWidth;
            const sy = (-pos.y + 1) / 2 * canvasHeight;

            const dx = sx - screenX;
            const dy = sy - screenY;
            const mesafe = Math.sqrt(dx * dx + dy * dy);

            // Pixel cinsinden hit alanı
            const hitPixel = this.handleBoyut * 1.5;

            if (mesafe < hitPixel && mesafe < enYakinMesafe) {
                enYakinMesafe = mesafe;
                bulunan = { tip, index };
            }
        }

        return bulunan;
    }

    /**
     * Handle'ın world pozisyonunu döndür (tip'e göre)
     */
    getHandleWorldPosition(tip: 'baslangic' | 'bitis' | 'orta'): THREE.Vector3 {
        if (!this._geometry) return new THREE.Vector3();

        const posAttr = this._geometry.getAttribute('position') as THREE.BufferAttribute;
        let index = 0;

        if (tip === 'bitis') index = 1;
        else if (tip === 'orta') index = 2;

        if (index >= posAttr.count) return new THREE.Vector3();

        return new THREE.Vector3(
            posAttr.getX(index),
            posAttr.getY(index),
            posAttr.getZ(index)
        );
    }

    /**
     * Kaynakları temizle
     */
    temizle(): void {
        this.handleTemizle();

        // Parent'tan kaldır
        if (this.parent) {
            this.parent.remove(this);
        }
    }
}

// ============================================================================
// ANA SINIF
// ============================================================================

/**
 * FikoYT 3D Çizgi Sınıfı
 *
 * Çeşitli yöntemlerle çizgi oluşturma imkanı sağlar:
 * - İki nokta arası
 * - Başlangıç + yön + uzunluk
 * - Başlangıç + açı + uzunluk
 * - Referans çizgi + açı
 * - Merkez + yön + uzunluk (çift yönlü)
 *
 * @extends FikoytKok3DElement
 */
export class FikoYT3dLine extends FikoytKok3DElement {
    /** Otomatik isimlendirme için sayaç */
    static SAYAC = 1;

    override type = 'FikoYT3dLine';

    // ========================================================================
    // ÖZELLİKLER
    // ========================================================================

    /** Başlangıç noktası */
    private _baslangic: THREE.Vector3 = new THREE.Vector3();

    /** Bitiş noktası */
    private _bitis: THREE.Vector3 = new THREE.Vector3();

    /** Çizgi ayarları */
    private _ayarlar: ICizgiAyarlari = { ...VARSAYILAN_AYARLAR };

    /** Three.js çizgi nesnesi (Line2, Line veya LineSegments) */
    private _cizgi: Line2 | THREE.Line | THREE.LineSegments | null = null;

    /** Ok noktaları (THREE.Points) - zoom bağımsız */
    private _okPoints: THREE.Points | null = null;

    /** Ok noktaları geometrisi */
    private _okGeometry: THREE.BufferGeometry | null = null;

    /** Ok noktaları materyali */
    private _okMaterial: THREE.PointsMaterial | null = null;

    /** Çizgi geometrisi (LineGeometry for Line2, BufferGeometry for others) */
    private _geometri: LineGeometry | THREE.BufferGeometry | null = null;

    /** Çizgi materyali (LineMaterial for Line2, LineBasicMaterial/LineDashedMaterial for others) */
    private _materyal: LineMaterial | THREE.LineBasicMaterial | THREE.LineDashedMaterial | null = null;

    /** Sağ tıklama menüsü */
    public sagMenu: any[] = [];

    // ========================================================================
    // GETTER / SETTER
    // ========================================================================

    /** Başlangıç noktası */
    get baslangic(): THREE.Vector3 { return this._baslangic.clone(); }
    set baslangic(deger: THREE.Vector3) {
        this._baslangic.copy(deger);
        this.cizgiyiGuncelle();
    }

    /** Bitiş noktası */
    get bitis(): THREE.Vector3 { return this._bitis.clone(); }
    set bitis(deger: THREE.Vector3) {
        this._bitis.copy(deger);
        this.cizgiyiGuncelle();
    }

    /** Çizgi uzunluğu */
    get uzunluk(): number {
        return this._baslangic.distanceTo(this._bitis);
    }

    /** Çizgi yönü (normalize edilmiş) */
    get yon(): THREE.Vector3 {
        return new THREE.Vector3().subVectors(this._bitis, this._baslangic).normalize();
    }

    /** Çizgi orta noktası */
    get ortaNokta(): THREE.Vector3 {
        return new THREE.Vector3().addVectors(this._baslangic, this._bitis).multiplyScalar(0.5);
    }

    /** Renk */
    get renk(): number | string { return this._ayarlar.renk!; }
    set renk(deger: number | string) {
        this._ayarlar.renk = deger;
        this.materyaliGuncelle();
        this.oklariGuncelle(); // Points rengini de güncelle
    }

    /** Kalınlık */
    get kalinlik(): number { return this._ayarlar.kalinlik!; }
    set kalinlik(deger: number) {
        this._ayarlar.kalinlik = deger;
        this.materyaliGuncelle();
    }

    /** Çizgi stili */
    get stil(): CizgiStili { return this._ayarlar.stil!; }
    set stil(deger: CizgiStili) {
        this._ayarlar.stil = deger;
        this.stilGuncelle();
    }

    /** Opaklık */
    get opaklık(): number { return this._ayarlar.opaklık!; }
    set opaklık(deger: number) {
        this._ayarlar.opaklık = Math.max(0, Math.min(1, deger));
        this.materyaliGuncelle();
    }

    /** Seçilebilirlik */
    get secilebilir(): boolean { return this._ayarlar.secilebilir!; }
    set secilebilir(deger: boolean) {
        this._ayarlar.secilebilir = deger;
        if (this._cizgi) {
            this._cizgi.userData['secilebilir'] = deger;
        }
    }

    /** Çizgi tipi */
    get cizgiTipi(): CizgiTipi { return this._ayarlar.cizgiTipi || 'Line2'; }
    set cizgiTipi(deger: CizgiTipi) {
        if (this._ayarlar.cizgiTipi === deger) return;
        this._ayarlar.cizgiTipi = deger;
        this.cizgiyiYenidenOlustur();
    }

    /** Oluşturma yöntemi */
    get yontem(): CizgiYontemi { return this._ayarlar.yontem || 'noktadanNoktaya'; }
    set yontem(deger: CizgiYontemi) {
        this._ayarlar.yontem = deger;
    }

    /** Kesikli aralık */
    get kesikliAralik(): number { return this._ayarlar.kesikliAralik!; }
    set kesikliAralik(deger: number) {
        this._ayarlar.kesikliAralik = deger;
        this.stilGuncelle();
    }

    /** Kesikli boyut */
    get kesikliBoyut(): number { return this._ayarlar.kesikliBoyut!; }
    set kesikliBoyut(deger: number) {
        this._ayarlar.kesikliBoyut = deger;
        this.stilGuncelle();
    }

    /** Ok başlangıç tipi */
    get okBaslangic(): OkTipi { return this._ayarlar.okBaslangic || 'yok'; }
    set okBaslangic(deger: OkTipi) {
        this._ayarlar.okBaslangic = deger;
        this.oklariOlustur(); // Tip değişince yeniden oluştur
    }

    /** Ok bitiş tipi */
    get okBitis(): OkTipi { return this._ayarlar.okBitis || 'yok'; }
    set okBitis(deger: OkTipi) {
        this._ayarlar.okBitis = deger;
        this.oklariOlustur(); // Tip değişince yeniden oluştur
    }

    /** Ok boyutu */
    get okBoyut(): number { return this._ayarlar.okBoyut!; }
    set okBoyut(deger: number) {
        this._ayarlar.okBoyut = deger;
        this.oklariGuncelle();
    }

    /** Katman */
    get katman(): number { return this._ayarlar.katman || 0; }
    set katman(deger: number) {
        this._ayarlar.katman = deger;
        if (this._cizgi) {
            this._cizgi.layers.set(deger);
        }
    }

    /**
     * En üstte göster - override
     * Base class traverse ile private _materyal ve _cizgi'ye erişemiyor,
     * bu yüzden doğrudan burada ayarlıyoruz
     */
    override get enUstte(): boolean { return this._ayarlar.enUstte || false; }
    override set enUstte(deger: boolean) {
        this._ayarlar.enUstte = deger;

        // Materyali güncelle
        if (this._materyal) {
            this._materyal.depthTest = !deger;
            this._materyal.depthWrite = !deger;
            this._materyal.transparent = deger || this._ayarlar.opaklık! < 1;

            // LineMaterial için alphaToCoverage kapatılmalı
            if ((this._materyal as any).alphaToCoverage !== undefined) {
                (this._materyal as any).alphaToCoverage = !deger;
            }

            this._materyal.needsUpdate = true;
        }

        // Çizgi render order
        if (this._cizgi) {
            this._cizgi.renderOrder = deger ? 999 : 0;
        }

        // Ok Points için de güncelle
        if (this._okPoints && this._okMaterial) {
            this._okPoints.renderOrder = deger ? 999 : 0;
            this._okMaterial.depthTest = !deger;
            this._okMaterial.depthWrite = !deger;
            this._okMaterial.needsUpdate = true;
        }

        // Parent (this) için de renderOrder ayarla
        this.renderOrder = deger ? 999 : 0;
    }

    /**
     * Render et - override
     * Base class traverse ile private _cizgi'ye erişemiyor
     */
    override get renderEt(): boolean { return this._ayarlar.renderEt !== false; }
    override set renderEt(deger: boolean) {
        this._ayarlar.renderEt = deger;

        // Çizgi görünürlüğünü ayarla
        if (this._cizgi) {
            this._cizgi.visible = deger;
            this._cizgi.frustumCulled = deger;
        }

        // Ok mesh'leri için de
        if (this._okPoints) {
            this._okPoints.visible = deger;
            this._okPoints.frustumCulled = deger;
        }

        // Ana objenin visible'ını da ayarla
        this.visible = deger;
    }

    // ========================================================================
    // CONSTRUCTOR
    // ========================================================================

    /**
     * Private constructor - Factory metodları kullanın
     */
    private constructor(sahne?: FikoSahne, isim?: string) {
        // İsim verilmemişse veya boşsa otomatik sayaçlı isim ata
        if (!isim || isim.trim() === '') {
            isim = "Çizgi." + FikoYT3dLine.SAYAC;
            FikoYT3dLine.SAYAC++;
        }
        super(sahne, isim);
        this.simge = '/simgeler/line.png';
        this.userData['FikoArayuz'].elementTip = 'Cizgi';
    }

    // ========================================================================
    // FACTORY METODLARI - ÇİZGİ OLUŞTURMA YÖNTEMLERİ
    // ========================================================================

    /**
     * İki nokta arasında çizgi oluşturur
     *
     * @param baslangic Başlangıç noktası
     * @param bitis Bitiş noktası
     * @param ayarlar Çizgi ayarları
     * @param sahne FikoSahne referansı
     * @param isim Çizgi ismi
     */
    static noktadanNoktaya(
        baslangic: THREE.Vector3,
        bitis: THREE.Vector3,
        ayarlar?: Partial<ICizgiAyarlari>,
        sahne?: FikoSahne,
        isim?: string
    ): FikoYT3dLine {
        const cizgi = new FikoYT3dLine(sahne, isim);
        cizgi._baslangic.copy(baslangic);
        cizgi._bitis.copy(bitis);
        cizgi.ayarlariUygula(ayarlar);
        cizgi.olustur();
        return cizgi;
    }

    /**
     * Başlangıç noktası, yön ve uzunluk ile çizgi oluşturur
     *
     * @param baslangic Başlangıç noktası
     * @param yon Yön vektörü (normalize edilecek)
     * @param uzunluk Çizgi uzunluğu
     * @param ayarlar Çizgi ayarları
     * @param sahne FikoSahne referansı
     * @param isim Çizgi ismi
     */
    static noktadanYonUzunluk(
        baslangic: THREE.Vector3,
        yon: THREE.Vector3,
        uzunluk: number,
        ayarlar?: Partial<ICizgiAyarlari>,
        sahne?: FikoSahne,
        isim?: string
    ): FikoYT3dLine {
        const cizgi = new FikoYT3dLine(sahne, isim);
        cizgi._baslangic.copy(baslangic);

        const normalizeYon = yon.clone().normalize();
        cizgi._bitis.copy(baslangic).addScaledVector(normalizeYon, uzunluk);

        cizgi.ayarlariUygula(ayarlar);
        cizgi.olustur();
        return cizgi;
    }

    /**
     * Başlangıç noktası, açı (XY düzleminde) ve uzunluk ile çizgi oluşturur
     *
     * @param baslangic Başlangıç noktası
     * @param aciDerece Açı (derece, X ekseninden)
     * @param uzunluk Çizgi uzunluğu
     * @param ayarlar Çizgi ayarları
     * @param sahne FikoSahne referansı
     * @param isim Çizgi ismi
     */
    static noktadanAciUzunluk(
        baslangic: THREE.Vector3,
        aciDerece: number,
        uzunluk: number,
        ayarlar?: Partial<ICizgiAyarlari>,
        sahne?: FikoSahne,
        isim?: string
    ): FikoYT3dLine {
        const aciRadyan = THREE.MathUtils.degToRad(aciDerece);
        const yon = new THREE.Vector3(Math.cos(aciRadyan), Math.sin(aciRadyan), 0);
        return FikoYT3dLine.noktadanYonUzunluk(baslangic, yon, uzunluk, ayarlar, sahne, isim);
    }

    /**
     * 3D açılar ile çizgi oluşturur (küresel koordinat)
     *
     * @param baslangic Başlangıç noktası
     * @param thetaDerece Theta açısı (XY düzleminde, derece)
     * @param phiDerece Phi açısı (Z ekseninden, derece)
     * @param uzunluk Çizgi uzunluğu
     * @param ayarlar Çizgi ayarları
     * @param sahne FikoSahne referansı
     * @param isim Çizgi ismi
     */
    static noktadan3DAci(
        baslangic: THREE.Vector3,
        thetaDerece: number,
        phiDerece: number,
        uzunluk: number,
        ayarlar?: Partial<ICizgiAyarlari>,
        sahne?: FikoSahne,
        isim?: string
    ): FikoYT3dLine {
        const theta = THREE.MathUtils.degToRad(thetaDerece);
        const phi = THREE.MathUtils.degToRad(phiDerece);

        const yon = new THREE.Vector3(
            Math.sin(phi) * Math.cos(theta),
            Math.sin(phi) * Math.sin(theta),
            Math.cos(phi)
        );

        return FikoYT3dLine.noktadanYonUzunluk(baslangic, yon, uzunluk, ayarlar, sahne, isim);
    }

    /**
     * Referans çizgiye göre açı ile çizgi oluşturur
     *
     * @param referans Referans çizgi
     * @param aciDerece Referansa göre açı (derece)
     * @param uzunluk Çizgi uzunluğu
     * @param baslangicNoktasi 'baslangic' | 'bitis' | 'orta' - Hangi noktadan başlayacak
     * @param ayarlar Çizgi ayarları
     * @param sahne FikoSahne referansı
     * @param isim Çizgi ismi
     */
    static referansIleAci(
        referans: FikoYT3dLine,
        aciDerece: number,
        uzunluk: number,
        baslangicNoktasi: 'baslangic' | 'bitis' | 'orta' = 'bitis',
        ayarlar?: Partial<ICizgiAyarlari>,
        sahne?: FikoSahne,
        isim?: string
    ): FikoYT3dLine {
        // Başlangıç noktasını belirle
        let baslangic: THREE.Vector3;
        switch (baslangicNoktasi) {
            case 'baslangic': baslangic = referans.baslangic; break;
            case 'orta': baslangic = referans.ortaNokta; break;
            case 'bitis':
            default: baslangic = referans.bitis; break;
        }

        // Referans yönünü al ve döndür
        const refYon = referans.yon;
        const aciRadyan = THREE.MathUtils.degToRad(aciDerece);

        // Z ekseni etrafında döndür
        const yeniYon = new THREE.Vector3(
            refYon.x * Math.cos(aciRadyan) - refYon.y * Math.sin(aciRadyan),
            refYon.x * Math.sin(aciRadyan) + refYon.y * Math.cos(aciRadyan),
            refYon.z
        );

        return FikoYT3dLine.noktadanYonUzunluk(baslangic, yeniYon, uzunluk, ayarlar, sahne, isim);
    }

    /**
     * Referans çizgiye dik çizgi oluşturur
     *
     * @param referans Referans çizgi
     * @param uzunluk Çizgi uzunluğu
     * @param baslangicNoktasi Hangi noktadan başlayacak
     * @param yon 'sol' | 'sag' - Dik yön
     * @param ayarlar Çizgi ayarları
     * @param sahne FikoSahne referansı
     * @param isim Çizgi ismi
     */
    static referansaDik(
        referans: FikoYT3dLine,
        uzunluk: number,
        baslangicNoktasi: 'baslangic' | 'bitis' | 'orta' = 'orta',
        yon: 'sol' | 'sag' = 'sag',
        ayarlar?: Partial<ICizgiAyarlari>,
        sahne?: FikoSahne,
        isim?: string
    ): FikoYT3dLine {
        const aci = yon === 'sag' ? 90 : -90;
        return FikoYT3dLine.referansIleAci(referans, aci, uzunluk, baslangicNoktasi, ayarlar, sahne, isim);
    }

    /**
     * Referans çizgiye paralel çizgi oluşturur
     *
     * @param referans Referans çizgi
     * @param mesafe Paralel mesafe
     * @param yon 'sol' | 'sag' - Hangi yöne paralel
     * @param ayarlar Çizgi ayarları
     * @param sahne FikoSahne referansı
     * @param isim Çizgi ismi
     */
    static referansaParalel(
        referans: FikoYT3dLine,
        mesafe: number,
        yon: 'sol' | 'sag' = 'sag',
        ayarlar?: Partial<ICizgiAyarlari>,
        sahne?: FikoSahne,
        isim?: string
    ): FikoYT3dLine {
        // Dik vektörü hesapla
        const refYon = referans.yon;
        const dikYon = new THREE.Vector3(-refYon.y, refYon.x, 0).normalize();

        if (yon === 'sol') dikYon.negate();

        const yeniBaslangic = referans.baslangic.clone().addScaledVector(dikYon, mesafe);
        const yeniBitis = referans.bitis.clone().addScaledVector(dikYon, mesafe);

        return FikoYT3dLine.noktadanNoktaya(yeniBaslangic, yeniBitis, ayarlar, sahne, isim);
    }

    /**
     * Merkez noktadan iki yöne çizgi oluşturur
     *
     * @param merkez Merkez nokta
     * @param yon Yön vektörü
     * @param toplamUzunluk Toplam uzunluk (merkezden her iki yöne yarısı)
     * @param ayarlar Çizgi ayarları
     * @param sahne FikoSahne referansı
     * @param isim Çizgi ismi
     */
    static merkezdenCift(
        merkez: THREE.Vector3,
        yon: THREE.Vector3,
        toplamUzunluk: number,
        ayarlar?: Partial<ICizgiAyarlari>,
        sahne?: FikoSahne,
        isim?: string
    ): FikoYT3dLine {
        const normalizeYon = yon.clone().normalize();
        const yarimUzunluk = toplamUzunluk / 2;

        const baslangic = merkez.clone().addScaledVector(normalizeYon, -yarimUzunluk);
        const bitis = merkez.clone().addScaledVector(normalizeYon, yarimUzunluk);

        return FikoYT3dLine.noktadanNoktaya(baslangic, bitis, ayarlar, sahne, isim);
    }

    /**
     * X ekseninde yatay çizgi oluşturur
     *
     * @param baslangicX Başlangıç X koordinatı
     * @param bitisX Bitiş X koordinatı
     * @param y Y koordinatı
     * @param z Z koordinatı
     * @param ayarlar Çizgi ayarları
     * @param sahne FikoSahne referansı
     * @param isim Çizgi ismi
     */
    static yatay(
        baslangicX: number,
        bitisX: number,
        y: number = 0,
        z: number = 0,
        ayarlar?: Partial<ICizgiAyarlari>,
        sahne?: FikoSahne,
        isim?: string
    ): FikoYT3dLine {
        return FikoYT3dLine.noktadanNoktaya(
            new THREE.Vector3(baslangicX, y, z),
            new THREE.Vector3(bitisX, y, z),
            ayarlar, sahne, isim
        );
    }

    /**
     * Y ekseninde dikey çizgi oluşturur
     *
     * @param baslangicY Başlangıç Y koordinatı
     * @param bitisY Bitiş Y koordinatı
     * @param x X koordinatı
     * @param z Z koordinatı
     * @param ayarlar Çizgi ayarları
     * @param sahne FikoSahne referansı
     * @param isim Çizgi ismi
     */
    static dikey(
        baslangicY: number,
        bitisY: number,
        x: number = 0,
        z: number = 0,
        ayarlar?: Partial<ICizgiAyarlari>,
        sahne?: FikoSahne,
        isim?: string
    ): FikoYT3dLine {
        return FikoYT3dLine.noktadanNoktaya(
            new THREE.Vector3(x, baslangicY, z),
            new THREE.Vector3(x, bitisY, z),
            ayarlar, sahne, isim
        );
    }

    /**
     * Z ekseninde derinlik çizgisi oluşturur
     *
     * @param baslangicZ Başlangıç Z koordinatı
     * @param bitisZ Bitiş Z koordinatı
     * @param x X koordinatı
     * @param y Y koordinatı
     * @param ayarlar Çizgi ayarları
     * @param sahne FikoSahne referansı
     * @param isim Çizgi ismi
     */
    static derinlik(
        baslangicZ: number,
        bitisZ: number,
        x: number = 0,
        y: number = 0,
        ayarlar?: Partial<ICizgiAyarlari>,
        sahne?: FikoSahne,
        isim?: string
    ): FikoYT3dLine {
        return FikoYT3dLine.noktadanNoktaya(
            new THREE.Vector3(x, y, baslangicZ),
            new THREE.Vector3(x, y, bitisZ),
            ayarlar, sahne, isim
        );
    }

    /**
     * Başka bir çizgiden kopya oluşturur
     *
     * @param kaynak Kaynak çizgi
     * @param sahne FikoSahne referansı
     * @param isim Yeni çizgi ismi
     */
    static kopya(
        kaynak: FikoYT3dLine,
        sahne?: FikoSahne,
        isim?: string
    ): FikoYT3dLine {
        return FikoYT3dLine.noktadanNoktaya(
            kaynak.baslangic,
            kaynak.bitis,
            { ...kaynak._ayarlar },
            sahne || kaynak.sahne,
            isim || `${kaynak.name}_kopya`
        );
    }

    /**
     * Koordinat dizisinden çizgi oluşturur
     *
     * @param koordinatlar [x1, y1, z1, x2, y2, z2] formatında dizi
     * @param ayarlar Çizgi ayarları
     * @param sahne FikoSahne referansı
     * @param isim Çizgi ismi
     */
    static koordinatlardan(
        koordinatlar: [number, number, number, number, number, number],
        ayarlar?: Partial<ICizgiAyarlari>,
        sahne?: FikoSahne,
        isim?: string
    ): FikoYT3dLine {
        return FikoYT3dLine.noktadanNoktaya(
            new THREE.Vector3(koordinatlar[0], koordinatlar[1], koordinatlar[2]),
            new THREE.Vector3(koordinatlar[3], koordinatlar[4], koordinatlar[5]),
            ayarlar, sahne, isim
        );
    }

    /**
     * JSON verisinden çizgi oluşturur
     *
     * @param json JSON verisi
     * @param sahne FikoSahne referansı
     */
    static jsondan(
        json: {
            baslangic: { x: number; y: number; z: number };
            bitis: { x: number; y: number; z: number };
            ayarlar?: Partial<ICizgiAyarlari>;
            isim?: string;
        },
        sahne?: FikoSahne
    ): FikoYT3dLine {
        return FikoYT3dLine.noktadanNoktaya(
            new THREE.Vector3(json.baslangic.x, json.baslangic.y, json.baslangic.z),
            new THREE.Vector3(json.bitis.x, json.bitis.y, json.bitis.z),
            json.ayarlar,
            sahne,
            json.isim
        );
    }

    // ========================================================================
    // DAHİLİ METODLAR
    // ========================================================================

    /**
     * Ayarları uygula
     */
    private ayarlariUygula(ayarlar?: Partial<ICizgiAyarlari>): void {
        if (ayarlar) {
            this._ayarlar = { ...VARSAYILAN_AYARLAR, ...ayarlar };
        }
    }

    /**
     * Çizgiyi oluştur
     */
    private olustur(): void {
        const cizgiTipi = this._ayarlar.cizgiTipi || 'Line2';

        // Materyal oluştur
        this.materyalOlustur();

        // Çizgi tipine göre geometri ve çizgi oluştur
        switch (cizgiTipi) {
            case 'Line2':
                this.olusturLine2();
                break;
            case 'Line':
                this.olusturLine();
                break;
            case 'LineSegments':
                this.olusturLineSegments();
                break;
            default:
                this.olusturLine2();
        }

        // Ortak ayarları uygula
        this.ortakAyarlariUygula();

        this.add(this._cizgi!);

        // Okları oluştur
        this.oklariOlustur();

        // Katman ayarla
        if (this._ayarlar.katman) {
            this._cizgi!.layers.set(this._ayarlar.katman);
        }

        // En üstte göster ayarı
        if (this._ayarlar.enUstte) {
            this.enUstte = true;
        }

        // Render et ayarı
        if (this._ayarlar.renderEt === false) {
            this.renderEt = false;
        }
    }

    /**
     * Çizgiyi yeniden oluştur (tip değiştiğinde)
     */
    private cizgiyiYenidenOlustur(): void {
        // Mevcut çizgiyi kaldır
        if (this._cizgi) {
            this.remove(this._cizgi);
            if (this._geometri) {
                this._geometri.dispose();
            }
            if (this._materyal) {
                this._materyal.dispose();
            }
        }

        // Ok Points'i kaldır
        this.okPointsTemizle();

        // Yeniden oluştur
        this.olustur();
    }

    /**
     * Line2 (kalın çizgi) oluştur
     */
    private olusturLine2(): void {
        const geometri = new LineGeometry();
        geometri.setPositions([
            this._baslangic.x, this._baslangic.y, this._baslangic.z,
            this._bitis.x, this._bitis.y, this._bitis.z
        ]);
        this._geometri = geometri;

        this._cizgi = new Line2(geometri, this._materyal as LineMaterial);
        (this._cizgi as Line2).computeLineDistances();
    }

    /**
     * THREE.Line (basit çizgi) oluştur
     */
    private olusturLine(): void {
        const geometri = new THREE.BufferGeometry();
        const positions = new Float32Array([
            this._baslangic.x, this._baslangic.y, this._baslangic.z,
            this._bitis.x, this._bitis.y, this._bitis.z
        ]);
        geometri.setAttribute('position', new THREE.BufferAttribute(positions, 3));
        this._geometri = geometri;

        this._cizgi = new THREE.Line(geometri, this._materyal as THREE.LineBasicMaterial | THREE.LineDashedMaterial);
        this._cizgi.computeLineDistances();
    }

    /**
     * THREE.LineSegments oluştur
     */
    private olusturLineSegments(): void {
        const geometri = new THREE.BufferGeometry();
        const positions = new Float32Array([
            this._baslangic.x, this._baslangic.y, this._baslangic.z,
            this._bitis.x, this._bitis.y, this._bitis.z
        ]);
        geometri.setAttribute('position', new THREE.BufferAttribute(positions, 3));
        this._geometri = geometri;

        this._cizgi = new THREE.LineSegments(geometri, this._materyal as THREE.LineBasicMaterial | THREE.LineDashedMaterial);
        this._cizgi.computeLineDistances();
    }

    /**
     * Ortak ayarları uygula (tüm çizgi tipleri için)
     */
    private ortakAyarlariUygula(): void {
        if (!this._cizgi) return;

        this._cizgi.userData['secilebilir'] = this._ayarlar.secilebilir;
        this._cizgi.userData['sahip'] = this;

        // En üstte ayarı için renderOrder
        if (this._ayarlar.enUstte) {
            this._cizgi.renderOrder = 999;
            this.renderOrder = 999;
        }

        // Çift tıklama ve sağ tıklama delegasyonu - child mesh'den parent'a
        const parentRef = this;

        // ciftTiklandi - FikoSecililer bu fonksiyonu çağırır
        Object.defineProperty(this._cizgi, 'ciftTiklandi', {
            value: function($event?: any) { return parentRef.ciftTiklandi($event); },
            configurable: true,
            writable: true
        });

        // Sağ tıklama menüsünü ayarla
        this.sagMenuAyarla();

        // sagMenu - Context menu için (FikoSecililer bunu kullanıyor)
        Object.defineProperty(this._cizgi, 'sagMenu', {
            get: function() { return parentRef.sagMenu; },
            configurable: true
        });

        // name - Sağ menü başlığında görünmesi için parent'ın name'ini kullan
        Object.defineProperty(this._cizgi, 'name', {
            get: function() { return parentRef.name; },
            set: function(v: string) { parentRef.name = v; },
            configurable: true
        });

        // Child objeyi ürün ağacında gizle (sadece parent görünsün)
        this._cizgi.userData = {
            FikoArayuz: {
                agactaGorunur: false,
                simge: this.userData?.FikoArayuz?.simge
            }
        };
    }

    /**
     * Materyal oluştur (çizgi tipine göre farklı materyal)
     */
    private materyalOlustur(): void {
        const renk = typeof this._ayarlar.renk === 'string'
            ? new THREE.Color(this._ayarlar.renk)
            : new THREE.Color(this._ayarlar.renk);

        const cizgiTipi = this._ayarlar.cizgiTipi || 'Line2';
        const isDashed = this._ayarlar.stil !== 'duz';
        const enUstte = this._ayarlar.enUstte || false;

        if (cizgiTipi === 'Line2') {
            // LineMaterial - gerçek kalınlık desteği sağlar
            // Not: enUstte için alphaToCoverage kapalı olmalı
            const mat = new LineMaterial({
                color: renk.getHex(),
                linewidth: this._ayarlar.kalinlik!,
                opacity: this._ayarlar.opaklık,
                transparent: this._ayarlar.opaklık! < 1 || enUstte,
                dashed: isDashed,
                dashSize: isDashed ? this.stileDashSize() : 0,
                gapSize: isDashed ? this._ayarlar.kesikliAralik : 0,
                resolution: getCanvasResolutionVec2(this.sahne?.anaGosterim?.sunucu),
                worldUnits: true,
                depthTest: !enUstte,
                depthWrite: !enUstte,
                alphaToCoverage: !enUstte
            });
            this._materyal = mat;
        } else {
            // LineBasicMaterial veya LineDashedMaterial
            if (isDashed) {
                this._materyal = new THREE.LineDashedMaterial({
                    color: renk.getHex(),
                    linewidth: this._ayarlar.kalinlik!,
                    opacity: this._ayarlar.opaklık,
                    transparent: this._ayarlar.opaklık! < 1 || enUstte,
                    dashSize: this.stileDashSize(),
                    gapSize: this._ayarlar.kesikliAralik!,
                    depthTest: !enUstte,
                    depthWrite: !enUstte
                });
            } else {
                this._materyal = new THREE.LineBasicMaterial({
                    color: renk.getHex(),
                    linewidth: this._ayarlar.kalinlik!,
                    opacity: this._ayarlar.opaklık,
                    transparent: this._ayarlar.opaklık! < 1 || enUstte,
                    depthTest: !enUstte,
                    depthWrite: !enUstte
                });
            }
        }
    }

    /**
     * Stile göre dash boyutu
     */
    private stileDashSize(): number {
        switch (this._ayarlar.stil) {
            case 'noktali': return 1;
            case 'kesikli-noktali': return this._ayarlar.kesikliBoyut! / 2;
            case 'kesikli':
            default: return this._ayarlar.kesikliBoyut!;
        }
    }

    /**
     * Ok Points'i oluştur (THREE.Points - zoom bağımsız)
     */
    private oklariOlustur(): void {
        // Mevcut points'i temizle
        this.okPointsTemizle();

        const baslangicOk = this._ayarlar.okBaslangic !== 'yok';
        const bitisOk = this._ayarlar.okBitis !== 'yok';

        if (!baslangicOk && !bitisOk) return;

        // Nokta pozisyonları
        const positions: number[] = [];
        const colors: number[] = [];
        const boyut = this._ayarlar.okBoyut || 5;

        const renk = typeof this._ayarlar.renk === 'string'
            ? new THREE.Color(this._ayarlar.renk)
            : new THREE.Color(this._ayarlar.renk);

        if (baslangicOk) {
            positions.push(this._baslangic.x, this._baslangic.y, this._baslangic.z);
            colors.push(renk.r, renk.g, renk.b);
        }

        if (bitisOk) {
            positions.push(this._bitis.x, this._bitis.y, this._bitis.z);
            colors.push(renk.r, renk.g, renk.b);
        }

        // Geometry oluştur
        this._okGeometry = new THREE.BufferGeometry();
        this._okGeometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
        this._okGeometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));

        // Material oluştur
        const enUstte = this._ayarlar.enUstte || false;
        this._okMaterial = new THREE.PointsMaterial({
            size: boyut,
            sizeAttenuation: false,  // Zoom'dan etkilenmez!
            vertexColors: true,
            depthTest: !enUstte,
            depthWrite: !enUstte,
            transparent: true,
            opacity: 0.95
        });

        // Points objesi
        this._okPoints = new THREE.Points(this._okGeometry, this._okMaterial);
        this._okPoints.name = 'LineEndPoints';
        this._okPoints.frustumCulled = false;
        this._okPoints.renderOrder = enUstte ? 999 : 0;
        this._okPoints.layers.enableAll();

        this.add(this._okPoints);
    }

    /**
     * Ok Points temizle
     */
    private okPointsTemizle(): void {
        if (this._okPoints) {
            this.remove(this._okPoints);
            this._okPoints = null;
        }
        if (this._okGeometry) {
            this._okGeometry.dispose();
            this._okGeometry = null;
        }
        if (this._okMaterial) {
            this._okMaterial.dispose();
            this._okMaterial = null;
        }
    }

    /**
     * Çizgiyi güncelle
     */
    private cizgiyiGuncelle(): void {
        if (!this._geometri) return;

        const cizgiTipi = this._ayarlar.cizgiTipi || 'Line2';

        if (cizgiTipi === 'Line2' && this._geometri instanceof LineGeometry) {
            // LineGeometry için setPositions kullan
            this._geometri.setPositions([
                this._baslangic.x, this._baslangic.y, this._baslangic.z,
                this._bitis.x, this._bitis.y, this._bitis.z
            ]);
        } else if (this._geometri instanceof THREE.BufferGeometry) {
            // BufferGeometry için position attribute güncelle
            const positions = this._geometri.getAttribute('position') as THREE.BufferAttribute;
            if (positions) {
                positions.setXYZ(0, this._baslangic.x, this._baslangic.y, this._baslangic.z);
                positions.setXYZ(1, this._bitis.x, this._bitis.y, this._bitis.z);
                positions.needsUpdate = true;
            }
        }

        if (this._cizgi) {
            this._cizgi.computeLineDistances();
        }

        this.oklariGuncelle();
    }

    /**
     * Materyali güncelle
     */
    private materyaliGuncelle(): void {
        if (!this._materyal) return;

        const renk = typeof this._ayarlar.renk === 'string'
            ? new THREE.Color(this._ayarlar.renk)
            : new THREE.Color(this._ayarlar.renk);

        const cizgiTipi = this._ayarlar.cizgiTipi || 'Line2';

        this._materyal.color.set(renk.getHex());
        this._materyal.opacity = this._ayarlar.opaklık!;
        this._materyal.transparent = this._ayarlar.opaklık! < 1;

        if (cizgiTipi === 'Line2' && this._materyal instanceof LineMaterial) {
            this._materyal.linewidth = this._ayarlar.kalinlik!;
            const _res = getCanvasResolution(this.sahne?.anaGosterim?.sunucu);
            this._materyal.resolution.set(_res.width, _res.height);
        }
        // Not: LineBasicMaterial ve LineDashedMaterial'da linewidth WebGL sınırlaması nedeniyle 1 pixel'den fazla olamaz

        this._materyal.needsUpdate = true;
    }

    /**
     * Stili güncelle (materyal değişimi gerektirir)
     */
    private stilGuncelle(): void {
        if (this._cizgi) {
            this.remove(this._cizgi);
            this._geometri?.dispose();
            this._materyal?.dispose();
        }
        this.olustur();
    }

    /**
     * Okları güncelle
     */
    private oklariGuncelle(): void {
        if (!this._okGeometry) {
            // Points henüz yok, yeniden oluştur
            this.oklariOlustur();
            return;
        }

        const positions = this._okGeometry.getAttribute('position') as THREE.BufferAttribute;
        const colors = this._okGeometry.getAttribute('color') as THREE.BufferAttribute;
        if (!positions) return;

        const baslangicOk = this._ayarlar.okBaslangic !== 'yok';
        const bitisOk = this._ayarlar.okBitis !== 'yok';

        // Renk güncelleme
        const renk = typeof this._ayarlar.renk === 'string'
            ? new THREE.Color(this._ayarlar.renk)
            : new THREE.Color(this._ayarlar.renk);

        let index = 0;
        if (baslangicOk) {
            positions.setXYZ(index, this._baslangic.x, this._baslangic.y, this._baslangic.z);
            if (colors) colors.setXYZ(index, renk.r, renk.g, renk.b);
            index++;
        }
        if (bitisOk && index < positions.count) {
            positions.setXYZ(index, this._bitis.x, this._bitis.y, this._bitis.z);
            if (colors) colors.setXYZ(index, renk.r, renk.g, renk.b);
        }

        positions.needsUpdate = true;
        if (colors) colors.needsUpdate = true;

        // Boyut değişikliği kontrolü
        if (this._okMaterial && this._okMaterial.size !== this._ayarlar.okBoyut) {
            this._okMaterial.size = this._ayarlar.okBoyut || 5;
            this._okMaterial.needsUpdate = true;
        }
    }

    // ========================================================================
    // TRANSFORMASYON METODLARI
    // ========================================================================

    /**
     * Çizgiyi taşı
     */
    tasi(delta: THREE.Vector3): this {
        this._baslangic.add(delta);
        this._bitis.add(delta);
        this.cizgiyiGuncelle();
        return this;
    }

    /**
     * Çizgiyi döndür (merkez etrafında)
     */
    dondur(aciDerece: number, eksen: THREE.Vector3 = new THREE.Vector3(0, 0, 1)): this {
        const merkez = this.ortaNokta;
        const aciRadyan = THREE.MathUtils.degToRad(aciDerece);

        this._baslangic.sub(merkez).applyAxisAngle(eksen, aciRadyan).add(merkez);
        this._bitis.sub(merkez).applyAxisAngle(eksen, aciRadyan).add(merkez);

        this.cizgiyiGuncelle();
        return this;
    }

    /**
     * Çizgiyi ölçekle (merkez etrafında)
     */
    olcekle(oran: number): this {
        const merkez = this.ortaNokta;

        this._baslangic.sub(merkez).multiplyScalar(oran).add(merkez);
        this._bitis.sub(merkez).multiplyScalar(oran).add(merkez);

        this.cizgiyiGuncelle();
        return this;
    }

    /**
     * Çizgiyi aynala
     */
    aynala(duzlem: 'x' | 'y' | 'z' | THREE.Plane): this {
        if (typeof duzlem === 'string') {
            switch (duzlem) {
                case 'x':
                    this._baslangic.x *= -1;
                    this._bitis.x *= -1;
                    break;
                case 'y':
                    this._baslangic.y *= -1;
                    this._bitis.y *= -1;
                    break;
                case 'z':
                    this._baslangic.z *= -1;
                    this._bitis.z *= -1;
                    break;
            }
        } else {
            // Düzleme göre aynala
            duzlem.projectPoint(this._baslangic, this._baslangic);
            duzlem.projectPoint(this._bitis, this._bitis);
        }

        this.cizgiyiGuncelle();
        return this;
    }

    /**
     * Çizgiyi uzat (başlangıç veya bitiş noktasından)
     */
    uzat(miktar: number, uctan: 'baslangic' | 'bitis' | 'her-iki' = 'bitis'): this {
        const yon = this.yon;

        switch (uctan) {
            case 'baslangic':
                this._baslangic.addScaledVector(yon, -miktar);
                break;
            case 'bitis':
                this._bitis.addScaledVector(yon, miktar);
                break;
            case 'her-iki':
                this._baslangic.addScaledVector(yon, -miktar / 2);
                this._bitis.addScaledVector(yon, miktar / 2);
                break;
        }

        this.cizgiyiGuncelle();
        return this;
    }

    /**
     * Çizgiyi kırp (başlangıç veya bitiş noktasından)
     */
    kirp(miktar: number, uctan: 'baslangic' | 'bitis' | 'her-iki' = 'bitis'): this {
        return this.uzat(-miktar, uctan);
    }

    // ========================================================================
    // HESAPLAMA METODLARI
    // ========================================================================

    /**
     * Noktanın çizgiye mesafesini hesapla
     */
    noktaMesafesi(nokta: THREE.Vector3): number {
        const cizgi3 = new THREE.Line3(this._baslangic, this._bitis);
        const enYakin = new THREE.Vector3();
        cizgi3.closestPointToPoint(nokta, true, enYakin);
        return nokta.distanceTo(enYakin);
    }

    /**
     * Noktanın çizgi üzerindeki en yakın noktasını bul
     */
    enYakinNokta(nokta: THREE.Vector3): THREE.Vector3 {
        const cizgi3 = new THREE.Line3(this._baslangic, this._bitis);
        const sonuc = new THREE.Vector3();
        cizgi3.closestPointToPoint(nokta, true, sonuc);
        return sonuc;
    }

    /**
     * Başka bir çizgi ile kesişim noktası (2D - XY düzlemi)
     */
    kesisimNoktasi2D(diger: FikoYT3dLine): THREE.Vector3 | null {
        const x1 = this._baslangic.x, y1 = this._baslangic.y;
        const x2 = this._bitis.x, y2 = this._bitis.y;
        const x3 = diger._baslangic.x, y3 = diger._baslangic.y;
        const x4 = diger._bitis.x, y4 = diger._bitis.y;

        const denom = (x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4);
        if (Math.abs(denom) < 1e-10) return null; // Paralel

        const t = ((x1 - x3) * (y3 - y4) - (y1 - y3) * (x3 - x4)) / denom;
        const u = -((x1 - x2) * (y1 - y3) - (y1 - y2) * (x1 - x3)) / denom;

        if (t >= 0 && t <= 1 && u >= 0 && u <= 1) {
            return new THREE.Vector3(
                x1 + t * (x2 - x1),
                y1 + t * (y2 - y1),
                (this._baslangic.z + this._bitis.z) / 2
            );
        }

        return null;
    }

    /**
     * İki çizgi arasındaki açıyı hesapla (derece)
     */
    aciHesapla(diger: FikoYT3dLine): number {
        const yon1 = this.yon;
        const yon2 = diger.yon;
        const dot = yon1.dot(yon2);
        const aciRadyan = Math.acos(THREE.MathUtils.clamp(dot, -1, 1));
        return THREE.MathUtils.radToDeg(aciRadyan);
    }

    /**
     * Çizginin belirli bir yüzdelik noktasını al
     */
    noktaAl(yuzde: number): THREE.Vector3 {
        const t = THREE.MathUtils.clamp(yuzde, 0, 1);
        return new THREE.Vector3().lerpVectors(this._baslangic, this._bitis, t);
    }

    /**
     * Çizgiyi eşit parçalara böl
     */
    bol(parcaSayisi: number): THREE.Vector3[] {
        const noktalar: THREE.Vector3[] = [];
        for (let i = 0; i <= parcaSayisi; i++) {
            noktalar.push(this.noktaAl(i / parcaSayisi));
        }
        return noktalar;
    }

    // ========================================================================
    // YAŞAM DÖNGÜSÜ
    // ========================================================================

    /**
     * Temizle
     */
    override temizle(): void {
        this._geometri?.dispose();
        this._materyal?.dispose();

        // Ok Points temizle
        this.okPointsTemizle();

        super.temizle();
    }

    /**
     * JSON'a dönüştür
     */
    override toJSON(): any {
        return {
            ...super.toJSON(),
            baslangic: { x: this._baslangic.x, y: this._baslangic.y, z: this._baslangic.z },
            bitis: { x: this._bitis.x, y: this._bitis.y, z: this._bitis.z },
            ayarlar: this._ayarlar
        };
    }

    // ========================================================================
    // ETKİLEŞİM METODLARI
    // ========================================================================

    /** Bu çizgi için açık dialog referansı */
    private _acikDialog?: any;

    /** Duplicate dialog açmayı önlemek için kilit */
    private _dialogAciliyor = false;

    /**
     * Dialog'u öne getirir - sayfadaki en yüksek z-index'i bulup +10 ekler
     */
    private dialoguOneGetir(dialog: any, dialogService: any): void {
        const dialogEl = document.getElementById(dialog.id_dialog);
        if (dialogEl) {
            // overlayZIndexAl: Sayfadaki TÜM elementleri tarar ve en yüksek z-index + 10 döndürür
            const yeniZIndex = dialogService.overlayZIndexAl?.() || 99999;

            // z-index'i hem dialog'a hem parent'a uygula (Semantic UI modal wrapper)
            dialogEl.style.setProperty('z-index', yeniZIndex.toString(), 'important');
            const parent = dialogEl.parentElement;
            if (parent) {
                parent.style.setProperty('z-index', yeniZIndex.toString(), 'important');
            }

            // Servisin internal z-index takibini de güncelle
            dialogService.dialogTiklandi?.(dialog);

            dialogEl.focus();
        }
    }

    /**
     * Çift tıklama - Düzenleme dialogunu aç
     */
    async ciftTiklandi($event?: any): Promise<void> {
        // Eğer dialog açma işlemi devam ediyorsa, tekrar çağırmayı engelle
        if (this._dialogAciliyor) {
            return;
        }

        if (!this.sahne?.anaGosterim?.DialogFiko) {
            return;
        }

        const dialogId = 'fiko-3d-line-edit-' + this.uuid;

        const dialogService = this.sahne.anaGosterim.DialogFiko;

        // 1. Önce _acikDialog referansını kontrol et
        if (this._acikDialog && this._acikDialog.miAcik) {
            // Dialog hala açık, servisin z-index yönetimini kullanarak öne getir
            this.dialoguOneGetir(this._acikDialog, dialogService);
            return;
        }

        // 2. DialogFiko servisinde aynı ID'li dialog var mı kontrol et
        const mevcutDialog = dialogService.idIleAl?.(dialogId);
        if (mevcutDialog && mevcutDialog.miAcik) {
            // Dialog zaten açık, servisin z-index yönetimini kullanarak öne getir
            this.dialoguOneGetir(mevcutDialog, dialogService);
            this._acikDialog = mevcutDialog;
            return;
        }

        // 3. Referansı temizle (dialog kapalı veya yok)
        this._acikDialog = undefined;

        // 4. Kilitle - async import sırasında duplicate çağrıları engelle
        this._dialogAciliyor = true;

        // Dialog component'i dinamik olarak import et
        const { FikoYT3dLineDialogComponent } = await import('./fikoyt-3d-line-dialog.component');

        const dialog = this.sahne.anaGosterim.DialogFiko.ac({
            bilesen: FikoYT3dLineDialogComponent,
            id: dialogId,
            $event: $event,
            ayarlar: {
                baslik: 'Çizgi Düzenle: ' + this.name,
                simge: 'pencil',
                modalMi: false,
                sinirlandi: false,
                boyut: { w: 220, h: 'auto' }
            },
            parametreler: {
                anaGosterim: this.sahne.anaGosterim,
                sahne: this.sahne,
                duzenleModu: true,
                mevcutCizgi: this
            }
        });

        // Dialog referansını sakla
        this._acikDialog = dialog;

        // Kilidi kaldır
        this._dialogAciliyor = false;

        // Dialog kapandığında referansı temizle
        if (dialog?.onDialogClose) {
            dialog.onDialogClose.subscribe(() => {
                this._acikDialog = undefined;
            });
        }
    }

    /**
     * Sağ tıklama menüsünü ayarla
     */
    private sagMenuAyarla(): void {
        this.sagMenu = [
            {
                simgeYazi: '👁‍🗨',
                baslik: 'Gizle / Göster',
                tiklandi: () => {
                    this.visible = !this.visible;
                    if (this.sahne?.anaGosterim?.FikoYT) {
                        this.sahne.anaGosterim.FikoYT.degisti.emit();
                    }
                }
            },
            {
                simgeYazi: '🖊',
                baslik: 'Düzenle',
                tiklandi: () => {
                    this.ciftTiklandi();
                }
            },
            {
                simgeYazi: '🎯',
                baslik: 'Ekrana Sığdır',
                tiklandi: () => {
                    this.ekranaSigdir();
                }
            },
            {
                simgeYazi: '📋',
                baslik: 'Kopyala',
                tiklandi: () => {
                    const kopya = this.kopyala();
                    if (this.parent) {
                        this.parent.add(kopya);
                    }
                }
            },
            {
                simgeYazi: '🗑',
                baslik: 'Sil',
                tiklandi: () => {
                    this.sil();
                }
            }
        ];
    }

    /**
     * Çizgiyi sil
     */
    sil(): void {
        // Parent referansını sakla (remove çağrıldıktan sonra null olacak)
        const parentRef = this.parent;

        // Parent'tan kaldır
        if (parentRef) {
            parentRef.remove(this);

            // Parent'ın liste array'inden de kaldır
            const parentAny = parentRef as any;
            if (parentAny.liste && Array.isArray(parentAny.liste)) {
                const index = parentAny.liste.indexOf(this);
                if (index > -1) {
                    parentAny.liste.splice(index, 1);
                }
            }
        }

        // Kaynakları temizle
        this.temizle();

    }

    /**
     * Çizgiyi ekrana sığdır (kamerayı çizgiye odakla)
     * NOT: FikoSahne.ekranaSigdir Line2 tipini desteklemiyor, özel implementasyon
     */
    ekranaSigdir(): void {
        const anaGosterim = this.sahne?.anaGosterim as any;
        if (!anaGosterim) {
            console.warn('[FikoYT3dLine] Ekrana sığdırma için anaGosterim bulunamadı');
            return;
        }

        // Bounding box'ı çizgi noktalarından hesapla
        const box = new THREE.Box3();
        box.expandByPoint(this._baslangic);
        box.expandByPoint(this._bitis);

        // Padding ekle
        const size = box.getSize(new THREE.Vector3());
        const padding = Math.max(size.length() * 0.5, 50);
        box.expandByScalar(padding);

        // Merkez ve yarıçap
        const center = box.getCenter(new THREE.Vector3());
        const radius = box.getSize(new THREE.Vector3()).length() / 2;

        // Kamera ve kontrol
        const kamera = anaGosterim.aktifKamera;
        const kontrol = anaGosterim.aktifKontrol;

        if (!kamera || !kontrol) {
            console.warn('[FikoYT3dLine] Kamera veya kontrol bulunamadı');
            return;
        }

        // Canvas boyutları
        const canvas = anaGosterim.canvas?.nativeElement;
        if (!canvas || canvas.clientWidth === 0) {
            console.warn('[FikoYT3dLine] Canvas bulunamadı');
            return;
        }

        const aspect = canvas.clientWidth / canvas.clientHeight;

        // TWEEN ile animasyonlu geçiş
        if (typeof TWEEN !== 'undefined') {
            anaGosterim.surekliCiz?.();

            // Yeni zoom hesapla
            const yakinlik = (aspect > 1.0)
                ? canvas.clientHeight / (radius * 2)
                : canvas.clientWidth / (radius * 2);

            // Kamera pozisyonunu merkeze göre ayarla
            const oncekiTarget = kontrol.target.clone();
            const kameraOffset = kamera.position.clone().sub(oncekiTarget);
            const kameraYeniPoz = center.clone().add(kameraOffset);

            // Kamera animasyonu
            new TWEEN.Tween({
                x: kamera.position.x,
                y: kamera.position.y,
                z: kamera.position.z,
                zoom: kamera.zoom
            })
            .to({
                x: kameraYeniPoz.x,
                y: kameraYeniPoz.y,
                z: kameraYeniPoz.z,
                zoom: yakinlik
            }, 400)
            .easing(TWEEN.Easing.Quadratic.Out)
            .onUpdate((val: any) => {
                kamera.position.set(val.x, val.y, val.z);
                kamera.zoom = val.zoom;
                kamera.updateProjectionMatrix();
            })
            .onComplete(() => {
                anaGosterim.surekliCizIptal?.();
            })
            .start();

            // Target animasyonu
            new TWEEN.Tween({
                x: kontrol.target.x,
                y: kontrol.target.y,
                z: kontrol.target.z
            })
            .to({
                x: center.x,
                y: center.y,
                z: center.z
            }, 400)
            .easing(TWEEN.Easing.Quadratic.Out)
            .onUpdate((val: any) => {
                kontrol.target.set(val.x, val.y, val.z);
                kontrol.update();
            })
            .start();

        } else {
            // TWEEN yoksa direkt ayarla
            kontrol.target.copy(center);
            kamera.lookAt(center);
            kontrol.update();
        }
    }

    /**
     * Çizgiyi kopyala
     */
    kopyala(): FikoYT3dLine {
        return FikoYT3dLine.kopya(this, this.sahne, this.name + '_kopya');
    }
}
